import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, Monitor, Moon, Sun } from "lucide-react";

import { AgentDropdown } from "@/components/agent-dropdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import type { Agent } from "@/hooks/use-agents";
import { useTheme } from "@/hooks/use-theme";
import {
  DEFAULT_SUBMIT_SHORTCUT,
  type SubmitShortcut,
  type UseUserPreferencesReturn,
  type UserPreferencesPatch,
} from "@/hooks/use-user-preferences";
import type { ThemePreference } from "@/lib/theme";

const THEME_OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  labelKey: string;
  icon: typeof Sun;
}> = [
  { value: "light", labelKey: "common:header.themeLight", icon: Sun },
  { value: "dark", labelKey: "common:header.themeDark", icon: Moon },
  { value: "system", labelKey: "common:header.themeSystem", icon: Monitor },
];

interface UserPreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preferencesApi: UseUserPreferencesReturn;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  isAdminUser: boolean;
}

/**
 * The user-facing editor for preferences. Server-backed settings refresh on
 * open and only a successful PATCH replaces their local persisted state;
 * theme remains local but follows the same draft-and-save interaction.
 */
export function UserPreferencesDialog({
  open,
  onOpenChange,
  preferencesApi,
  agents,
  agentsLoading,
  agentsError,
  isAdminUser,
}: UserPreferencesDialogProps) {
  const { t } = useTranslation();
  const { data, loading, saving, error, refresh, save } = preferencesApi;
  const { theme, setTheme } = useTheme();
  const [submitShortcut, setSubmitShortcut] = useState<SubmitShortcut>(
    DEFAULT_SUBMIT_SHORTCUT,
  );
  const [defaultAgentId, setDefaultAgentId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState("Europe/Zurich");
  const [themeDraft, setThemeDraft] = useState<ThemePreference>(theme);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  // The server response is the persisted source of truth after an initial
  // load, a save, or a later reopen. Draft changes stay local until Save.
  useEffect(() => {
    if (!open || !data) return;
    setSubmitShortcut(data.preferences.submit_shortcut);
    setDefaultAgentId(data.preferences.default_agent_id);
    setTimezone(data.preferences.timezone);
    setThemeDraft(theme);
  }, [data, open, theme]);

  const hasChanges = useMemo(() => {
    if (!data) return false;
    return submitShortcut !== data.preferences.submit_shortcut
      || defaultAgentId !== data.preferences.default_agent_id
      || timezone !== data.preferences.timezone
      || themeDraft !== theme;
  }, [data, defaultAgentId, submitShortcut, theme, themeDraft, timezone]);

  const handleSave = useCallback(async () => {
    if (!data) return;
    const patch: UserPreferencesPatch = {};
    if (submitShortcut !== data.preferences.submit_shortcut) {
      patch.submit_shortcut = submitShortcut;
    }
    if (defaultAgentId !== data.preferences.default_agent_id) {
      patch.default_agent_id = defaultAgentId;
    }
    if (timezone !== data.preferences.timezone) {
      patch.timezone = timezone;
    }
    if (await save(patch)) {
      setTheme(themeDraft);
      onOpenChange(false);
    }
  }, [data, defaultAgentId, onOpenChange, save, setTheme, submitShortcut, themeDraft, timezone]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="user-preferences-dialog">
        <DialogHeader>
          <DialogTitle>{t("preferences.title")}</DialogTitle>
          <DialogDescription>
            {t("preferences.description")}
          </DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground" aria-live="polite">
            <Spinner className="size-4" />
            {t("preferences.loading")}
          </div>
        ) : error && !data ? (
          <div className="flex min-h-40 flex-col items-center justify-center gap-4" role="alert">
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="size-4" />
              <span>{error}</span>
            </div>
            <Button type="button" variant="outline" onClick={() => void refresh()} disabled={loading}>
              {t("common:actions.retry")}
            </Button>
          </div>
        ) : data ? (
          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSave();
            }}
          >
            {error && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
                <span className="flex items-center gap-2">
                  <AlertCircle className="size-4" />
                  {error}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => void refresh()} disabled={loading || saving}>
                  {t("common:actions.retry")}
                </Button>
              </div>
            )}

            <fieldset disabled={saving} className="space-y-2">
              <legend className="text-sm font-medium">{t("preferences.sendMessages")}</legend>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50">
                <input
                  type="radio"
                  name="submit-shortcut"
                  value="enter"
                  checked={submitShortcut === "enter"}
                  onChange={() => setSubmitShortcut("enter")}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="block text-sm font-medium">{t("preferences.enter")}</span>
                  <span className="block text-xs text-muted-foreground">{t("preferences.enterHint")}</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50">
                <input
                  type="radio"
                  name="submit-shortcut"
                  value="ctrl_enter"
                  checked={submitShortcut === "ctrl_enter"}
                  onChange={() => setSubmitShortcut("ctrl_enter")}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="block text-sm font-medium">{t("preferences.ctrlEnter")}</span>
                  <span className="block text-xs text-muted-foreground">{t("preferences.ctrlEnterHint")}</span>
                </span>
              </label>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {t("common:header.theme")}
              </legend>
              {THEME_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50${
                      option.value === themeDraft ? " border-primary bg-muted/50" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="theme-preference"
                      value={option.value}
                      checked={option.value === themeDraft}
                      onChange={() => setThemeDraft(option.value)}
                      className="size-4 accent-primary"
                    />
                    <Icon className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t(option.labelKey)}
                    </span>
                  </label>
                );
              })}
            </fieldset>

            <section className="space-y-2" aria-labelledby="default-agent-heading">
              <div>
                <h3 id="default-agent-heading" className="text-sm font-medium">{t("preferences.defaultAgent")}</h3>
                <p className="text-xs text-muted-foreground">
                  {t("preferences.defaultAgentHint")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <AgentDropdown
                  agents={agents}
                  currentAgentId={defaultAgentId}
                  loading={agentsLoading}
                  error={agentsError}
                  disabled={saving}
                  onSelect={setDefaultAgentId}
                  isAdminUser={isAdminUser}
                />
                {defaultAgentId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving}
                    onClick={() => setDefaultAgentId(null)}
                  >
                    {t("preferences.useTenantDefault")}
                  </Button>
                )}
              </div>
            </section>

            <section className="space-y-2">
              <label htmlFor="account-timezone" className="text-sm font-medium">
                {t("preferences.timezone")}
              </label>
              <p className="text-xs text-muted-foreground">{t("preferences.timezoneHint")}</p>
              <Input
                id="account-timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                disabled={saving}
                autoComplete="off"
              />
            </section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                {t("common:actions.cancel")}
              </Button>
              <Button type="submit" disabled={!hasChanges || saving}>
                {saving && <Loader2 className="size-3 animate-spin" />}
                {saving ? t("connections:actions.saving") : t("common:actions.save")}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
